import type { MessageKey } from "../i18n/en";

type TFn = (key: MessageKey, params?: Record<string, string | number>) => string;

/** Compact damage/number formatting shared by the forecast and campaign pages:
 * 1.2M / 34k / 512. */
export function fmtDmg(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
  return String(Math.round(n));
}

/** Absolute date label from a ms epoch — e.g. "3 Jun 2026". Used for election dates. */
export function dateLabel(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** "Playing since" label from an ISO date — e.g. "Mar 2025", or "—" if unknown. */
export function sinceLabel(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/** "Closes in …" label for an open poll, from its close time (ms epoch).
 * Coarsens to days/hours/minutes; callers only render it while the poll is open. */
export function pollCountdown(endMs: number, t: TFn, nowMs: number = Date.now()): string {
  const mins = Math.floor((endMs - nowMs) / 60000);
  if (mins < 60) return t("polls.closesInMins", { n: Math.max(1, mins) });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("polls.closesInHours", { n: hrs });
  return t("polls.closesInDays", { n: Math.floor(hrs / 24) });
}

/** Coarse "last active" label from an ISO timestamp — minutes/hours/days ago. */
export function agoLabel(iso: string | null, t: TFn): string {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - Date.parse(iso)) / 60000);
  if (mins < 60) return t("common.minutesAgo", { n: Math.max(0, mins) });
  if (mins < 1440) return t("common.hoursAgo", { n: Math.floor(mins / 60) });
  return t("common.daysAgo", { n: Math.floor(mins / 1440) });
}
